import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAccountsDataView } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    getBudgetsDataView,
    getBudgetYears,
    getYearEnd,
} from "@insite/client-framework/Store/Data/Budgets/BudgetsSelectors";
import loadReviews from "@insite/client-framework/Store/Data/Budgets/Handlers/LoadReviews";
import updateBudget from "@insite/client-framework/Store/Data/Budgets/Handlers/UpdateBudget";
import { getCurrentShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { BudgetEnforcementLevel } from "@insite/client-framework/Store/Pages/BudgetManagement/BudgetManagementReducer";
import {
    isSearchUserSelectDisabled,
    isShipToAddressSelectDisabled,
} from "@insite/client-framework/Store/Pages/BudgetManagement/BudgetManagementSelectors";
import setDisplayedWidgetName from "@insite/client-framework/Store/Pages/BudgetManagement/Handlers/SetDisplayedWidgetName";
import updateLoadMaintenanceInfoParameter from "@insite/client-framework/Store/Pages/BudgetManagement/Handlers/UpdateLoadMaintenanceInfoParameter";
import updateMaintenanceInfo from "@insite/client-framework/Store/Pages/BudgetManagement/Handlers/UpdateMaintenanceInfo";
import translate from "@insite/client-framework/Translate";
import { AccountModel, BudgetModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import BudgetShipToDropdown from "@insite/content-library/Widgets/Budget/BudgetShipToDropdown";
import EnforcementLevelDisplay, {
    EnforcementLevelDisplayStyles,
} from "@insite/content-library/Widgets/Budget/EnforcementLevelDisplay";
import Button, { ButtonProps } from "@insite/mobius/Button";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Select, { SelectProps } from "@insite/mobius/Select";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import cloneDeep from "lodash/cloneDeep";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    displayedWidgetName: state.pages.budgetManagement.displayedWidgetName,
    accountsDataView: getAccountsDataView(state),
    shipTosDataView: getCurrentShipTosDataView(state),
    budgetDataView: getBudgetsDataView(state, state.pages.budgetManagement.getMaintenanceInfoParameter),
    billToState: getCurrentBillToState(state),
    getMaintenanceInfoParameter: state.pages.budgetManagement.getMaintenanceInfoParameter,
    maintenanceInfo: state.pages.budgetManagement.maintenanceInfo,
    budgetYear: state.pages.budgetManagement.getMaintenanceInfoParameter.fiscalYear,
    maintenanceUserId: state.pages.budgetManagement.getMaintenanceInfoParameter.userProfileId,
    maintenanceShipToId: state.pages.budgetManagement.getMaintenanceInfoParameter.shipToId,
    budgetYears: getBudgetYears(5),
    isSearchUserSelectDisabled: isSearchUserSelectDisabled(state),
    isShipToAddressSelectDisabled: isShipToAddressSelectDisabled(state),
    language: state.context.session.language,
});

const mapDispatchToProps = {
    setDisplayedWidgetName,
    updateLoadMaintenanceInfoParameter,
    loadReviews,
    updateBudget,
    updateMaintenanceInfo,
};

type Props = ReturnType<typeof mapStateToProps> & WidgetProps & ResolveThunks<typeof mapDispatchToProps>;

export interface AssignBudgetsStyles {
    gridContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonProps;
    saveButton?: ButtonProps;
    enforcementLevelGridItem?: GridItemProps;
    fullWidth?: GridItemProps;
    enforcementLevelDisplayStyles?: EnforcementLevelDisplayStyles;
    budgetYearGridItem?: GridItemProps;
    wrapper?: InjectableCss;
    budgetYearSelect?: SelectProps;
    assignBudgetInstructionsGridItem?: GridItemProps;
    assignBudgetInstructionsText?: TypographyPresentationProps;
    filterGridItem?: GridItemProps;
    selectShippingWrapper?: GridItemProps;
    searchUserWrapper?: InjectableCss;
    searchUserSelect?: SelectProps;
    shipToAddressSelect?: SelectProps;
    hiddenTable?: InjectableCss;
    tableGridItem?: GridItemProps;
    dataTable?: DataTableProps;
    periodHeader?: DataTableHeaderProps;
    endDateHeader?: DataTableHeaderProps;
    budgetAmountHeader?: DataTableHeaderProps;
    periodCells?: DataTableCellBaseProps;
    endDateCells?: DataTableCellBaseProps;
    budgetAmountCells?: DataTableCellBaseProps;
    currencySymbol?: TypographyPresentationProps;
    budgetAmountTextField?: TextFieldProps;
    buttonsHidden?: HiddenProps;
    assignBudgetMobile?: InjectableCss;
    gridItemCenter?: GridItemProps;
}

export const assignBudgetsStyles: AssignBudgetsStyles = {
    gridContainer: {
        gap: 0,
    },
    titleGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            padding: 15px;
        `,
    },
    titleText: {
        variant: "h4",
        css: css`
            margin: 0;
        `,
    },
    buttonsGridItem: {
        width: [12, 12, 4, 12, 12],
        css: css`
            padding: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: flex-start;
                    `,
                    css`
                        justify-content: flex-start;
                    `,
                    css`
                        justify-content: flex-end;
                    `,
                    css`
                        justify-content: flex-end;
                    `,
                    css`
                        justify-content: flex-end;
                    `,
                ])}
        `,
    },
    cancelButton: {
        variant: "tertiary",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 50%;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    saveButton: {
        css: css`
            margin-left: 10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 50%;
                    `,
                    css`
                        width: 50%;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    enforcementLevelGridItem: {
        width: 12,
        css: css`
            padding: 15px;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                            align-items: flex-start;
                            margin-bottom: 15px;
                            h3 {
                                margin: 0;
                            }
                        `,
                        css`
                            flex-direction: column;
                            align-items: flex-start;
                            margin-bottom: 15px;
                            h3 {
                                margin: 0;
                            }
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    enforcementLevelDisplayStyles: {
        labelText: {
            css: css`
                margin-bottom: 0;
            `,
        },
    },
    budgetYearGridItem: {
        width: [12, 12, 4, 3, 2],
    },
    assignBudgetInstructionsGridItem: {
        width: 12,
        css: css`
            margin-top: 10px !important;
        `,
    },
    filterGridItem: {
        width: [12, 12, 3, 3, 3],
        css: css`
            [data-test-selector="assignBudgetUserSelector"] {
                width: 100%;
                white-space: nowrap;
                text-overflow: ellipsis;
                padding-right: 20px !important;
            }
        `,
    },
    selectShippingWrapper: {
        width: [12, 12, 5, 5, 5],
        css: css`
            [data-test-selector="shipToSelector"] {
                width: 100%;
                white-space: nowrap;
                text-overflow: ellipsis;
                padding-right: 30px !important;
            }
        `,
    },
    searchUserWrapper: {
        css: css`
            display: inline-block;
            margin-right: 30px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 30px;
                    `,
                    css`
                        margin-bottom: 30px;
                    `,
                    css`
                        margin-bottom: 30px;
                    `,
                    css`
                        margin-bottom: 30px;
                    `,
                    null,
                ])}
        `,
    },
    hiddenTable: {
        css: css`
            width: 100%;
        `,
    },
    tableGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            padding: 15px;
            overflow: auto;
        `,
    },
    currencySymbol: {
        css: css`
            margin-right: 5px;
        `,
    },
    budgetAmountTextField: {
        cssOverrides: {
            formInputWrapper: css`
                width: 69px;
                input::-webkit-outer-spin-button,
                input::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }

                /* Firefox */
                input[type="number"] {
                    -moz-appearance: textfield;
                }

                input[type="number"] {
                    appearance: none;
                    -webkit-appearance: none;
                    -moz-appearance: none;
                }
            `,
            inputSelect: css`
                padding-right: 10px;
                border-radius: 3px;
                height: 30px;
                text-align: center;
                &:focus {
                    padding-right: 10px;
                }
            `,
        },
    },
    buttonsHidden: {
        css: css`
            display: flex;
            flex-basis: 100%;
        `,
    },
    fullWidth: {
        width: 12,
        css: css`
            flex-direction: column;
            padding: 15px;
        `,
    },
    assignBudgetMobile: {
        css: css`
            [class^="GridItemStyle"] {
                padding: 5px 0;
            }
            [class^="GridOffset"] {
                margin: 0;
                width: 100% !important;
            }
            padding: 15px;
            h4 {
                font-weight: bold;
            }
            &:nth-child(odd) {
                background: #f3f3f3;
            }
        `,
    },
    gridItemCenter: {
        align: "middle",
    },
};

const styles = assignBudgetsStyles;

function getEndDate(index: number, maintenanceInfo?: BudgetModel) {
    if (!maintenanceInfo) {
        return;
    }
    const review = maintenanceInfo.budgetLineCollection![index + 1];
    if (review) {
        const date = new Date(review.startDate);
        date.setDate(date.getDate() - 1);
        return date;
    }

    return getYearEnd(maintenanceInfo.fiscalYear, maintenanceInfo.fiscalYearEndDate!);
}

const AssignBudgets: React.FC<Props> = ({
    billToState,
    maintenanceUserId,
    maintenanceShipToId,
    accountsDataView,
    shipTosDataView,
    budgetDataView,
    setDisplayedWidgetName,
    displayedWidgetName,
    budgetYear,
    isSearchUserSelectDisabled,
    isShipToAddressSelectDisabled,
    updateBudget,
    budgetYears,
    loadReviews,
    updateLoadMaintenanceInfoParameter,
    getMaintenanceInfoParameter,
    updateMaintenanceInfo,
    maintenanceInfo, 
    language,
}) => {
    if (displayedWidgetName !== "AssignBudgets" || billToState.isLoading || !billToState.value) {
        return null;
    }

    const toasterContext = React.useContext(ToasterContext);

    if (!budgetDataView.isLoading && !budgetDataView.value) {
        loadReviews(getMaintenanceInfoParameter);
    }

    if (!budgetDataView.value) {
        return null;
    }

    if (!maintenanceInfo && budgetDataView.value) {
        updateMaintenanceInfo({ value: budgetDataView.value });
    }

    const handleChange = (event: React.FormEvent<HTMLSelectElement>, selectorType: string) => {
        updateLoadMaintenanceInfoParameter({
            userProfileId: selectorType === "user" ? event.currentTarget.value : "",
            shipToId: "",
            fiscalYear: getMaintenanceInfoParameter.fiscalYear,
        });
    };

    const selectShipToHandler = (id?: string) => {
        updateLoadMaintenanceInfoParameter({
            shipToId: id,
            userProfileId: "",
            fiscalYear: getMaintenanceInfoParameter.fiscalYear,
        });
    };

    const handleCancelButtonClick = () => {
        updateMaintenanceInfo({ value: undefined });
        setDisplayedWidgetName({ value: "ReviewBudget" });
        updateLoadMaintenanceInfoParameter({ fiscalYear: new Date().getFullYear() });
    };

    const handleSaveButtonClick = () => {
        updateBudget({
            updateBudgetApiParameter: { budget: maintenanceInfo! },
            onSuccess: onSaveSuccess,
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const onSaveSuccess = () => {
        updateMaintenanceInfo({ value: undefined });
        setDisplayedWidgetName({ value: "ReviewBudget" });
        toasterContext.addToast({ body: translate("Your changes were saved successfully."), messageType: "success" });
    };

    const handleBudgetYearChange = (event: React.FormEvent<HTMLSelectElement>) => {
        updateLoadMaintenanceInfoParameter({ fiscalYear: Number(event.currentTarget.value) });
    };

    const handleBudgetAmountChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value === "" ? undefined : Number(event.currentTarget.value);
        if (maintenanceInfo) {
            const currentBudget = cloneDeep(maintenanceInfo);
            currentBudget.budgetLineCollection![index].currentFiscalYearBudget = value;
            updateMaintenanceInfo({ value: currentBudget });
        }
    };

    const handleBudgetOnBlur = () => {
        if (maintenanceInfo) {
            const currentBudget = cloneDeep(maintenanceInfo);
            currentBudget.budgetLineCollection?.forEach(budgetLine => {
                if (!budgetLine.currentFiscalYearBudget) {
                    budgetLine.currentFiscalYearBudget = 0;
                }
            });
            updateMaintenanceInfo({ value: currentBudget });
        }
    };

    const accounts = accountsDataView.value || [];
    const shipTos = shipTosDataView.value ? shipTosDataView.value.filter(o => o.customerSequence !== "-1") : [];
    const tableRows =
        maintenanceInfo?.budgetLineCollection?.map((budgetLine, index) => {
            const endDate = getEndDate(index, budgetDataView.value);
            return {
                period: budgetLine.period,
                endDate: endDate
                    ? getLocalizedDateTime({
                          dateTime: endDate,
                          language,
                      })
                    : "",
                budgetAmount: budgetLine.currentFiscalYearBudget,
            };
        }) || [];
    const [searchUserWrapperStyles] = React.useState(() => mergeToNew(styles.wrapper, styles.searchUserWrapper));
    const enforcementLevel = billToState.value.budgetEnforcementLevel as keyof typeof BudgetEnforcementLevel;

    return (
        <>
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.titleGridItem}>
                    <Typography as="h2" {...styles.titleText}>
                        {translate("Assign Budgets")}
                    </Typography>
                </GridItem>
                <Hidden below="lg">
                    <GridItem {...styles.buttonsGridItem}>
                        <Button {...styles.cancelButton} onClick={handleCancelButtonClick}>
                            {translate("Cancel")}
                        </Button>
                        <Button
                            {...styles.saveButton}
                            onClick={handleSaveButtonClick}
                            data-test-selector="saveAssignedBudgetsButton"
                        >
                            {translate("Save")}
                        </Button>
                    </GridItem>
                </Hidden>
                <GridItem {...styles.enforcementLevelGridItem}>
                    <EnforcementLevelDisplay
                        enforcementLevel={BudgetEnforcementLevel[enforcementLevel]}
                        extendedStyles={styles.enforcementLevelDisplayStyles}
                    ></EnforcementLevelDisplay>
                </GridItem>
                <GridItem {...styles.fullWidth}>
                    <GridContainer>
                        <GridItem {...styles.budgetYearGridItem}>
                            <Select
                                label={translate("Select Budget Year")}
                                {...styles.budgetYearSelect}
                                value={budgetYear}
                                onChange={handleBudgetYearChange}
                                data-test-selector="assignBudgetYearSelector"
                            >
                                {budgetYears.map((budgetYear: number) => (
                                    <option key={budgetYear} value={budgetYear}>
                                        {budgetYear}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.filterGridItem}>
                            <Select
                                label={translate("Select User")}
                                {...styles.searchUserSelect}
                                value={maintenanceUserId}
                                disabled={isSearchUserSelectDisabled}
                                onChange={(event: React.FormEvent<HTMLSelectElement>) => handleChange(event, "user")}
                                data-test-selector="assignBudgetUserSelector"
                            >
                                <option value="">{translate("Select User")}</option>,
                                {accounts.map((account: AccountModel) => (
                                    <option key={account.id} value={account.id}>
                                        {account.userName}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.selectShippingWrapper}>
                            <Select
                                label={translate("Select Shipping Address")}
                                {...styles.shipToAddressSelect}
                                value={maintenanceShipToId}
                                disabled={isShipToAddressSelectDisabled}
                                onChange={(event: React.FormEvent<HTMLSelectElement>) => handleChange(event, "shipTo")}
                                data-test-selector="assignBudgetShipToSelector"
                            >
                                <option value="">{translate("Select Ship To")}</option>,
                                {shipTos.map((shipTo: ShipToModel) => (
                                    <option key={shipTo.id.toString()} value={shipTo.id.toString()}>
                                        {shipTo.label}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                    </GridContainer>
                    <GridContainer>
                        <GridItem {...styles.assignBudgetInstructionsGridItem}>
                            <Typography {...styles.assignBudgetInstructionsText}>
                                {siteMessage("Budgets_AssignBudgetsInstructions")}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <Hidden below="lg" {...styles.hiddenTable}>
                    <GridItem {...styles.tableGridItem}>
                        {!budgetDataView.isLoading && tableRows.length > 0 && (
                            <DataTable {...styles.dataTable}>
                                <DataTableHead>
                                    <DataTableHeader {...styles.periodHeader}>{translate("Period")}</DataTableHeader>
                                    <DataTableHeader {...styles.endDateHeader}>{translate("End Date")}</DataTableHeader>
                                    <DataTableHeader {...styles.budgetAmountHeader}>
                                        {translate("Budget Amount")}
                                    </DataTableHeader>
                                </DataTableHead>
                                <DataTableBody>
                                    {tableRows.map(({ period, endDate, budgetAmount }, index) => (
                                        <DataTableRow key={period} data-test-selector="assignBudgetsPeriodRow">
                                            <DataTableCell {...styles.periodCells}>{period}</DataTableCell>
                                            <DataTableCell {...styles.endDateCells}>{endDate}</DataTableCell>
                                            <DataTableCell {...styles.budgetAmountCells}>
                                                <Typography {...styles.currencySymbol}>
                                                    {billToState.value.customerCurrencySymbol}
                                                </Typography>
                                                <TextField
                                                    type="number"
                                                    defaultValue="0"
                                                    value={budgetAmount}
                                                    {...styles.budgetAmountTextField}
                                                    data-test-selector={`budgetAmountField_${index}`}
                                                    onChange={budgetAmount =>
                                                        handleBudgetAmountChange(index, budgetAmount)
                                                    }
                                                    onBlur={handleBudgetOnBlur}
                                                />
                                            </DataTableCell>
                                        </DataTableRow>
                                    ))}
                                </DataTableBody>
                            </DataTable>
                        )}
                    </GridItem>
                </Hidden>
            </GridContainer>
            <Hidden above="md">
                {tableRows.map(({ period, endDate, budgetAmount }, index) => (
                    <StyledWrapper {...styles.assignBudgetMobile} key={period}>
                        <GridContainer>
                            <GridItem width={6}>{translate("Period")}</GridItem>
                            <GridItem width={6}>{period}</GridItem>
                        </GridContainer>
                        <GridContainer>
                            <GridItem width={6}>{translate("End Date")}</GridItem>
                            <GridItem width={6}>{endDate}</GridItem>
                        </GridContainer>
                        <GridContainer>
                            <GridItem width={6}>{translate("Budget Amount")}</GridItem>
                            <GridItem width={6} {...styles.gridItemCenter}>
                                <Typography {...styles.currencySymbol}>
                                    {billToState.value.customerCurrencySymbol}
                                </Typography>
                                <TextField
                                    type="number"
                                    value={budgetAmount}
                                    {...styles.budgetAmountTextField}
                                    data-test-selector={`budgetAmountField_${index}`}
                                    onChange={budgetAmount => handleBudgetAmountChange(index, budgetAmount)}
                                />
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>
                ))}
            </Hidden>
            <GridItem {...styles.buttonsGridItem}>
                <Hidden {...styles.buttonsHidden} above="md">
                    <Button {...styles.cancelButton} onClick={handleCancelButtonClick}>
                        {translate("Cancel")}
                    </Button>
                    <Button {...styles.saveButton} onClick={handleSaveButtonClick}>
                        {translate("Save")}
                    </Button>
                </Hidden>
            </GridItem>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AssignBudgets),
    definition: {
        group: "BudgetManagement",
        allowedContexts: ["BudgetManagementPage"],
    },
};

export default widgetModule;
